import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil, distinctUntilChanged, tap } from 'rxjs/operators';

// Configs
import { SnackBarConfig } from '../../../shared/configs';

// Services
import { RfqApiService } from '../../../shared/services/api/rfq/rfq-api.service';

@Component({
  selector: 'lum-rfq-variant-form',
  templateUrl: './rfq-variant-form.component.html',
  styleUrls: ['./rfq-variant-form.component.scss'],
})
export class RfqVariantFormComponent implements OnInit, OnDestroy {
  form :UntypedFormGroup;
  rfqs :any = [];
  displayValue :string = '';

  loading$ :Observable<boolean>;
  submittingForm$ :Observable<boolean>;
  rfqs$ :Observable<any>;

  private searchUpdated$ = new Subject<string>();
  private onDestroy$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data :{
      variant :any;
      featureFlag :any;
      productType ?:string;
      discontinued ?:number;
    },
    private dialog :MatDialogRef<RfqVariantFormComponent>,
    private fb :UntypedFormBuilder,
    private rfqApiService :RfqApiService,
    private snackBar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.initForm();
    this.getDataFromService();
    this.onSearchUpdate().subscribe();
    this.getRfqsFromObservable().subscribe();
  }

  ngOnDestroy() :void {
    this.rfqApiService.setRfqs(null);
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onClose() :void {
    this.dialog.close();
  }

  onSearchKeywordChange(event :any) :void {
    this.searchUpdated$.next(event.target.value);
  }

  onSelectOption(event :MatAutocompleteSelectedEvent) :void {
    const sku = event.option.value;
    const variants = sku?.variant_skus || [];
    this.form.patchValue({
      variant_parent_id: sku.id,
      parent_sku: sku,
      product_name: sku.product_name,
      internal_sku: `${sku.internal_sku || sku.sku}-${variants.length + 1}`,
      wholesale_price: sku.wholesale_price,
      retail_price: sku.retail_price,
    });
  }

  displayFn(option :any) :string {
    return option && (option.internal_sku || option.sku)
      ? `${option.internal_sku || option.sku}[${option.product_name}]`
      : '';
  }

  onSubmit() :void {
    this.rfqApiService
      .createVariant(this.form.value)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: () => {
          this.snackBar.open('Variant SKU created successfully.', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.onClose();
        },
        error: (err) => {
          if (err.message) {
            this.snackBar.open(err.message, 'Error', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          } else {
            this.snackBar.open(
              'Error saving record. Please try again later or contact support.',
              'Error',
              {
                duration: SnackBarConfig.DEFAULT.duration,
              },
            );
          }
        },
      });
  }

  private initForm() :void {
    this.form = this.fb.group({
      internal_sku: ['', Validators.required],
      product_name: ['', Validators.required],
      wholesale_price: [0, Validators.required],
      retail_price: [0, Validators.required],
      variant_parent_id: [null],
      parent_sku: [this.fb.group({})],
    });
  }

  private getDataFromService() :void {
    this.rfqs$ = this.rfqApiService.rfqs$;
    this.loading$ = this.rfqApiService.loading$;
    this.submittingForm$ = this.rfqApiService.submittingForm$;
  }

  private onSearchUpdate() :Observable<string> {
    const params = {
      parent_only: 'true',
      include_variants: 'true',
      factoryId: '0',
    };
    const productType = this.data?.productType || null;
    const discontinued = this.data?.discontinued;

    if (productType) {
      params['productType'] = productType;
    }

    if (discontinued !== undefined && discontinued !== null) {
      params['discontinued'] = discontinued;
    }

    return this.searchUpdated$.asObservable().pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap((keyword :string) => {
        this.rfqApiService.plainSearch({
          ...params,
          sku: keyword,
        });
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getRfqsFromObservable() :Observable<any> {
    return this.rfqs$.pipe(
      tap((rfqs :any) => {
        this.rfqs = rfqs;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  get mode() :string {
    return this.data?.variant ? 'Update' : 'Create';
  }

  get newKitsStructureEnabled() :boolean {
    return this.data?.featureFlag?.newKitsStructureEnabled;
  }

  get variantParentIdControl() :UntypedFormControl {
    return this.form.get('variant_parent_id') as UntypedFormControl;
  }

  get internalSkuControl() :UntypedFormControl {
    return this.form.get('internal_sku') as UntypedFormControl;
  }

  get productNameControl() :UntypedFormControl {
    return this.form.get('product_name') as UntypedFormControl;
  }

  get wholeSalePriceControl() :UntypedFormControl {
    return this.form.get('wholesale_price') as UntypedFormControl;
  }

  get retailPriceControl() :UntypedFormControl {
    return this.form.get('retail_price') as UntypedFormControl;
  }

  get hasInvalidInput() :boolean {
    const invalid =
      this.form.get('internal_sku')?.invalid ||
      this.form.get('product_name')?.invalid ||
      this.form.get('wholesale_price')?.invalid ||
      this.form.get('retail_price')?.invalid;

    return invalid || false;
  }
}
